import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ModuleConfig, User } from '../../types/config';
import { useTenantCurrency } from '../../context/TenantCurrencyContext';
import { canUserOpenModule } from '../../utils/moduleAccess';
import { dedupeNavigationModules } from '../../utils/navigationDedup';

interface SidebarProps {
  config: ModuleConfig[];
  sidebarConfig: {
    enabled: boolean;
    collapsible: boolean;
    defaultCollapsed: boolean;
    width: string;
  };
  user: User;
}

type CollapsedTooltip = {
  id: string;
  label: string;
  top: number;
};

const COLLAPSED_STORAGE_KEY = 'sidebar-collapsed';

function readCollapsedPreference(defaultCollapsed: boolean): boolean {
  try {
    const saved = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (saved === 'true') return true;
    if (saved === 'false') return false;
  } catch {
    // ignore storage errors
  }
  return defaultCollapsed;
}

export const Sidebar: React.FC<SidebarProps> = ({ config, sidebarConfig, user }) => {
  const { currencyCode } = useTenantCurrency();
  const [collapsed, setCollapsed] = useState(() =>
    readCollapsedPreference(sidebarConfig.defaultCollapsed)
  );
  const [collapsedTooltip, setCollapsedTooltip] = useState<CollapsedTooltip | null>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const pathname = location.pathname || '/';

  const showCollapsedTooltip = useCallback(
    (event: React.MouseEvent<HTMLElement>, id: string, label: string) => {
      if (!collapsed) return;
      const rect = event.currentTarget.getBoundingClientRect();
      setCollapsedTooltip({
        id,
        label,
        top: rect.top + rect.height / 2,
      });
    },
    [collapsed]
  );

  const hideCollapsedTooltip = useCallback(() => {
    setCollapsedTooltip(null);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
    } catch {
      // ignore storage errors
    }
  }, [collapsed]);

  useEffect(() => {
    if (!sidebarConfig.enabled || !sidebarConfig.collapsible || collapsed) return;
    const onDocClick = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        setCollapsed(true);
        setCollapsedTooltip(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [collapsed, sidebarConfig.collapsible, sidebarConfig.enabled]);

  if (!sidebarConfig.enabled) return null;

  const filteredModules = dedupeNavigationModules(
    config.filter((module) => canUserOpenModule(module, user))
  );

  const iconClassFor = (module: ModuleConfig) => {
    const map: Record<string, string> = {
      LayoutDashboard: 'fa-tachometer-alt',
      Users: 'fa-users',
      GraduationCap: 'fa-chalkboard-teacher',
      BookOpen: 'fa-book',
      Calendar: 'fa-calendar',
      CalendarClock: 'fa-user-clock',
      FileText: 'fa-file-alt',
      Settings: 'fa-cog',
      Code: 'fa-code',
      UserCog: 'fa-user-tie',
      Truck: 'fa-bus',
      Shield: 'fa-shield-alt',
      Building: 'fa-school',
      DollarSign: 'fa-dollar-sign',
      Library: 'fa-book-reader',
      ClipboardList: 'fa-tasks',
      Award: 'fa-award',
      Certificate: 'fa-certificate',
      Package: 'fa-boxes',
      Hotel: 'fa-bed',
      Utensils: 'fa-utensils',
      ShoppingCart: 'fa-shopping-cart',
      Receipt: 'fa-file-invoice-dollar',
      Wallet: 'fa-wallet',
      MessageSquare: 'fa-comments',
      Megaphone: 'fa-bullhorn',
      ArrowRightLeft: 'fa-exchange-alt',
      Lock: 'fa-lock',
      StickyNote: 'fa-sticky-note',
      ClipboardCheck: 'fa-clipboard-check',
      Lightbulb: 'fa-lightbulb',
      BarChart2: 'fa-chart-bar',
      UserPlus: 'fa-user-plus',
      Globe: 'fa-globe',
    };
    if (module.id === 'student-portal') return 'fa-user-graduate';
    if (module.id === 'fee' || module.path === '/fees') {
      if (currencyCode === 'INR') return 'fa-rupee-sign';
      if (currencyCode === 'GBP') return 'fa-pound-sign';
      if (currencyCode === 'EUR') return 'fa-euro-sign';
      return 'fa-wallet';
    }
    return map[module.icon] || 'fa-circle';
  };

  const isActivePath = (modulePath: string) =>
    modulePath === pathname ||
    (modulePath === '/' && pathname === '/') ||
    (modulePath !== '/' && pathname.startsWith(`${modulePath}/`));

  const toggleCollapse = () => {
    if (sidebarConfig.collapsible) {
      setCollapsed(!collapsed);
      setCollapsedTooltip(null);
    }
  };

  const collapsedSidebarWidth = '4rem';
  const sidebarWidth = collapsed ? collapsedSidebarWidth : sidebarConfig.width;

  return (
    <aside
      ref={sidebarRef}
      className="relative flex flex-col bg-secondary-800 border-r border-secondary-700/80 shadow-sm transition-all duration-300 overflow-hidden"
      style={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        maxWidth: sidebarWidth,
        flexShrink: 0,
      }}
      onMouseLeave={hideCollapsedTooltip}
    >
      <div className="flex flex-col h-full min-h-0 min-w-0">
        <div
          className={`flex items-center px-3 py-4 border-b border-white/5 ${
            collapsed ? 'justify-center' : 'justify-between'
          }`}
        >
          {!collapsed && (
            <span className="text-sm font-medium text-secondary-300 tracking-wide">Navigation</span>
          )}
          {sidebarConfig.collapsible && (
            <button
              type="button"
              onClick={toggleCollapse}
              onMouseEnter={(e) =>
                collapsed ? showCollapsedTooltip(e, 'sidebar-toggle', 'Expand menu') : undefined
              }
              className="p-2 text-secondary-300 hover:text-secondary-100 hover:bg-white/5 rounded-lg transition-colors"
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            >
              <i className={`fas ${collapsed ? 'fa-chevron-right' : 'fa-chevron-left'} text-sm`} />
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2">
          <ul className="space-y-0.5">
            {filteredModules.map((module) => {
              const isActive = isActivePath(module.path);
              return (
                <li key={module.id}>
                  <Link
                    to={module.path}
                    onMouseEnter={(e) => showCollapsedTooltip(e, module.id, module.name)}
                    onFocus={(e) => showCollapsedTooltip(e, module.id, module.name)}
                    className={`group w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      isActive
                        ? 'bg-white/10 text-royal-ivory border-l-[3px] border-primary-400/60'
                        : 'text-secondary-300 hover:bg-white/5 hover:text-secondary-100 border-l-[3px] border-transparent'
                    } ${collapsed ? 'justify-center px-2 sidebar-collapsed-item' : ''}`}
                    aria-label={collapsed ? module.name : undefined}
                  >
                    <i
                      className={`fas ${iconClassFor(module)} w-5 text-center ${
                        isActive ? 'text-primary-300' : 'text-secondary-400 group-hover:text-secondary-200'
                      } ${collapsed ? '' : 'mr-3'}`}
                    />
                    {!collapsed && <span>{module.name}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {!collapsed && (
          <div className="px-4 py-3 border-t border-white/5">
            <p className="text-[10px] uppercase tracking-widest text-secondary-500 text-center">Excellence in Education</p>
          </div>
        )}
      </div>

      {collapsed && collapsedTooltip ? (
        <div
          className="sidebar-water-tooltip"
          style={{ top: collapsedTooltip.top, left: collapsedSidebarWidth }}
          role="tooltip"
          aria-hidden
        >
          <span className="sidebar-water-tooltip-text">{collapsedTooltip.label}</span>
        </div>
      ) : null}
    </aside>
  );
};
